<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, ApiError } from '../api/client'
import type { Taxonomy } from '../api/types'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import ImportDialog from '../components/ImportDialog.vue'
import TextPreviewModal from '../components/TextPreviewModal.vue'
import { IconPlus, IconTrash, IconUpload } from '../components/Icons'
import Modal from '../components/Modal.vue'
import { Table, Td, Th, Tr } from '../components/Table'
import { useToast } from '../lib/toast'
import EpisodeSubtitleDialog from '../components/dialogs/Subtitles.vue'
import { Badge, Button, EmptyState, ErrorState, Input, Panel, SkeletonRows, cx } from '../components/ui'
import { localeName } from '../lib/format'
import { useAsync, useTitle } from '../lib/hooks'

type PendingDelete =
  | { kind: 'show' }
  | { kind: 'locale'; locale: string }
  | { kind: 'episode'; episode: number }
  | { kind: 'subtitle'; episode: number; locale: string }

type SubPreview = { episode: number; locale: string }

const route = useRoute()
const router = useRouter()
const toast = useToast()

const id = computed(() => String(route.params.id ?? ''))
const showId = computed(() => Number(id.value))
useTitle(computed(() => `剧 #${id.value}`))

const season = ref<number | null>(null)
const newSeason = ref('')
const addingSeason = ref(false)
const importOpen = ref(false)
const subsFor = ref<number | null>(null)
const pending = ref<PendingDelete | null>(null)
const busy = ref(false)
const expanded = ref<string | null>(null)
const subPreview = ref<SubPreview | null>(null)
const previewText = ref('')
const previewLoading = ref(false)
const previewError = ref('')

const editTax = ref(false)
const draftGenres = ref<string[]>([])
const draftTropes = ref<string[]>([])
const taxBusy = ref(false)

const validId = computed(() => Number.isInteger(showId.value) && showId.value > 0)

const detail = useAsync(
  () => (validId.value ? api.showDetail(showId.value) : Promise.resolve(null)),
  () => [showId.value, validId.value],
)

const taxonomies = useAsync(() => api.listTaxonomies(), () => [])

const genreOptions = computed(() => taxonomies.data.value?.taxonomies.filter((t) => t.kind === 'genre') ?? [])
const tropeOptions = computed(() => taxonomies.data.value?.taxonomies.filter((t) => t.kind === 'trope') ?? [])

const nameBySlug = computed(() => {
  const map = new Map<string, Taxonomy>()
  for (const t of taxonomies.data.value?.taxonomies ?? []) map.set(`${t.kind}:${t.slug}`, t)
  return map
})

function labelOf(kind: 'genre' | 'trope', slug: string): string {
  return nameBySlug.value.get(`${kind}:${slug}`)?.name_zh ?? slug
}

const episodes = useAsync(
  () =>
    !validId.value || season.value === null
      ? Promise.resolve(null)
      : api.listSeasonEpisodes(showId.value, season.value),
  () => [validId.value, showId.value, season.value],
)

const querySeason = computed(() => {
  const raw = route.query.season
  const n = Number(Array.isArray(raw) ? raw[0] : raw)
  return Number.isInteger(n) && n > 0 ? n : null
})
const partialImport = computed(() => route.query.partial === '1')

watch(
  () => detail.data.value,
  (info) => {
    if (!info) return
    if (querySeason.value != null) {
      season.value = querySeason.value
      return
    }
    if (season.value !== null) return
    if (info.seasons.length > 0) season.value = info.seasons[0].season
  },
)

watch(querySeason, (n) => {
  if (n != null) season.value = n
})

watch(
  [subPreview, season, showId],
  ([preview, s, sid], _prev, onCleanup) => {
    if (!preview || s === null || !Number.isInteger(sid) || sid <= 0) return
    let cancelled = false
    onCleanup(() => {
      cancelled = true
    })
    previewLoading.value = true
    previewError.value = ''
    previewText.value = ''
    void api
      .subtitleContent(sid, s, preview.episode, preview.locale)
      .then((text) => {
        if (!cancelled) previewText.value = text
      })
      .catch((err) => {
        if (!cancelled) previewError.value = err instanceof ApiError ? err.message : '加载失败'
      })
      .finally(() => {
        if (!cancelled) previewLoading.value = false
      })
  },
)

const info = computed(() => detail.data.value)
const list = computed(() => episodes.data.value?.episodes ?? [])
const subCount = computed(() => list.value.reduce((sum, e) => sum + (e.subtitle_locales?.length ?? 0), 0))
const notFound = computed(() => detail.error.value?.status === 404)

function reloadAll() {
  detail.reload()
  episodes.reload()
}

function startEditTax() {
  if (!info.value) return
  draftGenres.value = [...(info.value.genres ?? [])]
  draftTropes.value = [...(info.value.tropes ?? [])]
  editTax.value = true
}

function toggleSlug(kind: 'genre' | 'trope', slug: string) {
  const list = kind === 'genre' ? draftGenres : draftTropes
  const max = kind === 'genre' ? 5 : 20
  const i = list.value.indexOf(slug)
  if (i >= 0) {
    list.value = list.value.filter((s) => s !== slug)
    return
  }
  if (list.value.length >= max) {
    toast.fail(`最多选 ${max} 个`)
    return
  }
  list.value = [...list.value, slug]
}

async function saveTaxonomies() {
  if (!info.value) return
  if (draftGenres.value.length < 1) {
    toast.fail('至少选择 1 个类型')
    return
  }
  taxBusy.value = true
  try {
    await api.upsertShow(showId.value, undefined, {
      genres: draftGenres.value,
      tropes: draftTropes.value,
    })
    toast.ok('题材已保存')
    editTax.value = false
    detail.reload()
  } catch (err) {
    toast.fail(err instanceof ApiError ? err.message : '保存失败')
  } finally {
    taxBusy.value = false
  }
}

async function confirmDelete() {
  if (!pending.value) return
  busy.value = true
  try {
    const p = pending.value
    if (p.kind === 'show') {
      await api.deleteShow(showId.value)
      toast.ok(`剧 #${showId.value} 已删除`)
      await router.push('/shows')
      return
    }
    if (p.kind === 'locale') {
      await api.deleteShowLocale(showId.value, p.locale)
      toast.ok(`已删除 ${p.locale}`)
      detail.reload()
    } else if (p.kind === 'episode' && season.value !== null) {
      await api.deleteEpisode(showId.value, season.value, p.episode)
      toast.ok(`已删除第 ${p.episode} 集`)
      reloadAll()
    } else if (p.kind === 'subtitle' && season.value !== null) {
      await api.deleteSub(showId.value, season.value, p.episode, p.locale)
      toast.ok(`已删除 ${p.locale}`)
      reloadAll()
    }
    pending.value = null
  } catch (err) {
    toast.fail(err instanceof ApiError ? err.message : '删除失败')
  } finally {
    busy.value = false
  }
}

function goToNewSeason() {
  const value = Number(newSeason.value)
  if (!Number.isInteger(value) || value < 1) {
    toast.fail('季号须为正整数')
    return
  }
  season.value = value
  addingSeason.value = false
  newSeason.value = ''
  importOpen.value = true
}

const deleteTitle = computed(() => {
  const p = pending.value
  if (!p) return ''
  if (p.kind === 'show') return `删除剧 #${showId.value}？`
  if (p.kind === 'locale') return `删除 ${p.locale}？`
  if (p.kind === 'episode') return `删除第 ${p.episode} 集？`
  return `删除 ${p.locale} 字幕？`
})
</script>

<template>
  <template v-if="!validId">
    <PageHeader title="无效 ID" :breadcrumb="[{ label: '剧', to: '/shows' }, { label: id }]" />
    <PageBody>
      <Panel>
        <EmptyState :title="`“${id}” 不是合法 ID`">
          <template #action>
            <Button size="sm" @click="router.push('/shows')">返回</Button>
          </template>
        </EmptyState>
      </Panel>
    </PageBody>
  </template>

  <template v-else-if="notFound">
    <PageHeader title="不存在" :breadcrumb="[{ label: '剧', to: '/shows' }, { label: `#${showId}` }]" />
    <PageBody>
      <Panel>
        <EmptyState :title="`找不到剧 #${showId}`">
          <template #action>
            <Button size="sm" @click="router.push('/shows')">返回</Button>
          </template>
        </EmptyState>
      </Panel>
    </PageBody>
  </template>

  <template v-else>
    <PageHeader
      :title="`剧 #${showId}`"
      :subtitle="
        info
          ? `${info.locales.length} 语言 · ${info.seasons.length} 季 · ${info.episode_count} 集 · ${info.subtitle_count} 字幕`
          : undefined
      "
      :breadcrumb="[{ label: '剧', to: '/shows' }, { label: `#${showId}` }]"
    >
      <template #actions>
        <Button size="sm" variant="primary" :disabled="!info" @click="importOpen = true">
          <IconUpload :size="13" />
          导入
        </Button>
        <Button size="sm" variant="danger" :disabled="!info" @click="pending = { kind: 'show' }">
          <IconTrash :size="13" />
          删除
        </Button>
      </template>
    </PageHeader>

    <PageBody>
      <Panel v-if="detail.error.value">
        <ErrorState :message="detail.error.value.message" @retry="detail.reload" />
      </Panel>
      <div v-else-if="!info" class="flex flex-col gap-4">
        <div class="panel h-[160px] animate-pulse" />
        <div class="panel h-[240px] animate-pulse" />
      </div>
      <div v-else class="flex flex-col gap-4">
        <Panel title="题材">
          <template #actions>
            <Button v-if="!editTax" size="sm" @click="startEditTax">编辑</Button>
            <template v-else>
              <Button size="sm" :disabled="taxBusy" @click="editTax = false">取消</Button>
              <Button size="sm" variant="primary" :loading="taxBusy" @click="saveTaxonomies">保存</Button>
            </template>
          </template>
          <div v-if="!editTax" class="px-4 py-3">
            <div
              v-if="(info.genres?.length ?? 0) === 0 && (info.tropes?.length ?? 0) === 0"
              class="text-[13px] text-dim"
            >
              尚未设置类型或标签
            </div>
            <div v-else class="flex flex-col gap-2">
              <div v-if="(info.genres?.length ?? 0) > 0" class="flex flex-wrap items-center gap-1.5">
                <span class="text-[11px] text-dim w-10 shrink-0">类型</span>
                <Badge v-for="g in info.genres" :key="g" tone="accent">{{ labelOf('genre', g) }}</Badge>
              </div>
              <div v-if="(info.tropes?.length ?? 0) > 0" class="flex flex-wrap items-center gap-1.5">
                <span class="text-[11px] text-dim w-10 shrink-0">标签</span>
                <Badge v-for="t in info.tropes" :key="t" tone="info">{{ labelOf('trope', t) }}</Badge>
              </div>
            </div>
          </div>
          <div v-else class="flex flex-col gap-4 px-4 py-3">
            <div>
              <div class="mb-2 text-[12px] text-dim">类型（必选，最多 5 个）</div>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="opt in genreOptions"
                  :key="opt.slug"
                  type="button"
                  :class="
                    cx(
                      'rounded-sm border px-2 py-1 text-[12px] transition-colors',
                      draftGenres.includes(opt.slug)
                        ? 'border-accent/50 bg-accent/15 text-accent-dim'
                        : 'border-line bg-raised text-muted hover:border-accent/30',
                    )
                  "
                  @click="toggleSlug('genre', opt.slug)"
                >
                  {{ opt.name_zh }}
                </button>
              </div>
            </div>
            <div>
              <div class="mb-2 text-[12px] text-dim">标签（最多 20 个）</div>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="opt in tropeOptions"
                  :key="opt.slug"
                  type="button"
                  :class="
                    cx(
                      'rounded-sm border px-2 py-1 text-[12px] transition-colors',
                      draftTropes.includes(opt.slug)
                        ? 'border-link/50 bg-link/10 text-link'
                        : 'border-line bg-raised text-muted hover:border-link/30',
                    )
                  "
                  @click="toggleSlug('trope', opt.slug)"
                >
                  {{ opt.name_zh }}
                </button>
              </div>
            </div>
          </div>
        </Panel>

        <Panel title="语言">
          <EmptyState v-if="info.locales.length === 0" title="无语言详情">
            <template #action>
              <Button size="sm" @click="importOpen = true">导入</Button>
            </template>
          </EmptyState>
          <ul v-else class="divide-y divide-line">
            <li v-for="l in info.locales" :key="l.locale" class="px-4 py-3">
              <div class="flex items-start justify-between gap-3">
                <button
                  type="button"
                  class="min-w-0 flex-1 text-left"
                  @click="expanded = expanded === l.locale ? null : l.locale"
                >
                  <div class="flex items-center gap-2">
                    <Badge tone="neutral">{{ l.locale }}</Badge>
                    <span class="text-[12px] text-dim">{{ localeName(l.locale) }}</span>
                  </div>
                  <p class="mt-1 text-[13px] font-medium">{{ l.title }}</p>
                  <p
                    v-if="expanded === l.locale && l.description"
                    class="mt-1.5 whitespace-pre-wrap text-[12px] leading-relaxed text-muted"
                  >
                    {{ l.description }}
                  </p>
                </button>
                <Button
                  size="sm"
                  variant="ghost"
                  :aria-label="`删除 ${l.locale}`"
                  @click="pending = { kind: 'locale', locale: l.locale }"
                >
                  <IconTrash :size="13" />
                </Button>
              </div>
            </li>
          </ul>
        </Panel>

        <Panel title="集与字幕">
          <div class="flex flex-wrap items-center gap-1.5 border-b border-line px-4 py-2.5">
            <p v-if="partialImport && querySeason" class="w-full px-4 pb-1 text-[12px] text-warn">
              导入在第 {{ querySeason }} 季中断，已写入的详情和字幕不会回滚，请核对该季。
            </p>
            <span v-if="info.seasons.length === 0" class="text-[12.5px] text-dim">还没有季</span>
            <button
              v-for="s in info.seasons"
              :key="s.season"
              type="button"
              :class="
                cx(
                  'flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12.5px] transition-colors',
                  s.season === querySeason && partialImport
                    ? 'border-warn/50 bg-warn/10 font-medium text-text'
                    : s.season === season
                      ? 'border-accent/40 bg-accent/8 font-medium text-text'
                      : 'border-line text-muted hover:bg-hover hover:text-text',
                )
              "
              @click="season = s.season"
            >
              第 {{ s.season }} 季
              <span class="tnum text-[11px] text-dim">{{ s.episodes.length }}</span>
            </button>

            <span v-if="addingSeason" class="flex items-center gap-1.5">
              <Input
                v-model="newSeason"
                class="w-[74px] px-2 py-1 font-mono text-[12px]"
                placeholder="季号"
                aria-label="新季季号"
                @keydown.enter="goToNewSeason"
                @keydown.escape="addingSeason = false"
              />
              <Button size="sm" variant="primary" @click="goToNewSeason">确定</Button>
              <Button size="sm" variant="ghost" @click="addingSeason = false">取消</Button>
            </span>
            <button
              v-else
              type="button"
              class="flex items-center gap-1 rounded-md border border-dashed border-line-strong px-2.5 py-1 text-[12.5px] text-muted hover:bg-hover hover:text-text"
              @click="addingSeason = true"
            >
              <IconPlus :size="12" />
              新季
            </button>
          </div>

          <EmptyState v-if="season === null" title="选择或新建一个季" />
          <ErrorState
            v-else-if="episodes.error.value"
            :message="episodes.error.value.message"
            @retry="episodes.reload"
          />
          <SkeletonRows v-else-if="episodes.initial.value" :rows="5" :cols="3" />
          <EmptyState v-else-if="list.length === 0" :title="`第 ${season} 季还没有集`">
            <template #action>
              <Button size="sm" variant="primary" @click="importOpen = true">导入字幕</Button>
            </template>
          </EmptyState>
          <template v-else>
            <div class="border-b border-line px-4 py-2 text-[12px] text-dim">
              <span class="tnum">{{ list.length }} 集 · {{ subCount }} 条字幕</span>
            </div>
            <Table>
              <template #head>
                <Tr>
                  <Th class="w-[64px]">集</Th>
                  <Th>字幕</Th>
                  <Th class="w-[120px]">操作</Th>
                </Tr>
              </template>
              <Tr v-for="e in list" :key="e.id">
                <Td mono class="font-medium">{{ String(e.episode).padStart(2, '0') }}</Td>
                <Td>
                  <span v-if="e.subtitle_locales && e.subtitle_locales.length > 0" class="flex flex-wrap gap-1">
                    <span v-for="loc in e.subtitle_locales" :key="loc" class="inline-flex items-center gap-0.5">
                      <button
                        type="button"
                        class="rounded-sm transition-opacity hover:opacity-80"
                        :title="`预览 ${loc}`"
                        @click="subPreview = { episode: e.episode, locale: loc }"
                      >
                        <Badge tone="info">{{ loc }}</Badge>
                      </button>
                      <button
                        type="button"
                        :aria-label="`删除第 ${e.episode} 集的 ${loc} 字幕`"
                        class="text-dim hover:text-danger"
                        @click="pending = { kind: 'subtitle', episode: e.episode, locale: loc }"
                      >
                        <IconTrash :size="11" />
                      </button>
                    </span>
                  </span>
                  <span v-else class="text-[12px] text-dim">—</span>
                </Td>
                <Td>
                  <span class="flex items-center gap-1">
                    <Button size="sm" @click="subsFor = e.episode">字幕</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      :aria-label="`删除第 ${e.episode} 集`"
                      @click="pending = { kind: 'episode', episode: e.episode }"
                    >
                      <IconTrash :size="13" />
                    </Button>
                  </span>
                </Td>
              </Tr>
            </Table>
          </template>
        </Panel>
      </div>
    </PageBody>

    <ImportDialog
      :open="importOpen"
      :locked-show-id="showId"
      @close="importOpen = false"
      @done="
        () => {
          importOpen = false
          reloadAll()
        }
      "
    />

    <EpisodeSubtitleDialog
      v-if="season !== null && subsFor !== null"
      open
      :show-id="showId"
      :season="season"
      :episode="subsFor"
      :existing-locales="list.find((e) => e.episode === subsFor)?.subtitle_locales ?? []"
      @close="subsFor = null"
      @saved="reloadAll"
    />

    <Modal :open="pending !== null" :title="deleteTitle" @close="pending = null">
      <template #footer>
        <Button @click="pending = null">取消</Button>
        <Button variant="danger" :loading="busy" @click="void confirmDelete()">删除</Button>
      </template>
      <p class="text-[13px] text-muted">此操作不可恢复。</p>
    </Modal>

    <TextPreviewModal
      :open="subPreview !== null"
      :title="
        subPreview && season !== null
          ? `S${String(season).padStart(2, '0')}E${String(subPreview.episode).padStart(2, '0')}.${subPreview.locale}`
          : ''
      "
      :hint="subPreview ? localeName(subPreview.locale) : undefined"
      :content="previewText"
      :loading="previewLoading"
      :error="previewError"
      @close="subPreview = null"
    />
  </template>
</template>
